import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Plus, Pencil, Trash2, Tags, RefreshCw, ChevronDown, ChevronRight, FolderTree, Layers, Sparkles } from 'lucide-react';

const CategoriasPage = () => {
  const { empresaActual } = useApp();
  const [categoriasJerarquicas, setCategoriasJerarquicas] = useState([]);
  const [categoriasPadre, setCategoriasPadre] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', padre_id: null, activo: true });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [initConfirm, setInitConfirm] = useState(false);
  const [expandedPadres, setExpandedPadres] = useState({});

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const [jerarquicasRes, padresRes] = await Promise.all([
        api.get('/categorias/jerarquicas', { params: { empresa_id: empresaActual.id } }),
        api.get('/categorias', { params: { empresa_id: empresaActual.id, solo_padres: true } })
      ]);
      setCategoriasJerarquicas(jerarquicasRes.data);
      setCategoriasPadre(padresRes.data);
      
      // Expandir todas las categorías padre por defecto
      const expanded = {};
      jerarquicasRes.data.forEach(cat => { expanded[cat.id] = true; });
      setExpandedPadres(expanded);
    } catch (error) {
      toast.error('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = () => { 
    setFormData({ nombre: '', padre_id: null, activo: true }); 
    setEditingItem(null); 
  };

  const handleOpenDialog = (item = null, options = {}) => {
    const { isPadre = false, preselectedPadreId = null } = options;
    if (item) { 
      setEditingItem(item); 
      setFormData({ nombre: item.nombre, padre_id: item.padre_id, activo: item.activo }); 
    } else { 
      resetForm(); 
      if (preselectedPadreId) {
        // Pre-seleccionar categoría padre para nueva subcategoría
        setFormData({ nombre: '', padre_id: preselectedPadreId, activo: true });
      } else if (!isPadre && categoriasPadre.length > 0) {
        setFormData({ nombre: '', padre_id: categoriasPadre[0].id, activo: true });
      }
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre) { toast.error('El nombre es requerido'); return; }
    const payload = { ...formData, empresa_id: empresaActual.id };
    try {
      if (editingItem) { 
        await api.put(`/categorias/${editingItem.id}`, payload); 
        toast.success('Categoría actualizada'); 
      } else { 
        await api.post('/categorias', payload); 
        toast.success('Categoría creada'); 
      }
      setDialogOpen(false); 
      resetForm(); 
      loadData();
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Error al guardar'); 
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try { 
      await api.delete(`/categorias/${deleteConfirm.id}`); 
      toast.success(deleteConfirm.padre_id ? 'Subcategoría eliminada' : 'Categoría padre y subcategorías eliminadas'); 
      setDeleteConfirm(null);
      loadData(); 
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Error al eliminar'); 
    }
  };

  const handleInicializar = async () => {
    try {
      setLoading(true);
      const res = await api.post('/categorias/inicializar', null, { params: { empresa_id: empresaActual.id } });
      toast.success(res.data.message);
      setInitConfirm(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al inicializar categorías');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (padreId) => {
    setExpandedPadres(prev => ({ ...prev, [padreId]: !prev[padreId] }));
  };

  if (!empresaActual) return <div className="text-center py-12 text-gray-500">Selecciona una empresa</div>;

  return (
    <div className="space-y-6" data-testid="categorias-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FolderTree className="w-8 h-8 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Categorías de Gastos</h1>
            <p className="text-sm text-gray-500">Organización jerárquica de categorías</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          {categoriasJerarquicas.length === 0 && (
            <Button variant="outline" onClick={() => setInitConfirm(true)} className="border-amber-300 text-amber-700 hover:bg-amber-50">
              <Sparkles className="w-4 h-4 mr-2" />
              Inicializar Categorías
            </Button>
          )}
          <Button onClick={() => handleOpenDialog(null, { isPadre: true })} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Categoría Padre
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Layers className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Categorías Padre</p>
                <p className="text-2xl font-bold">{categoriasJerarquicas.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Tags className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Subcategorías</p>
                <p className="text-2xl font-bold">{categoriasJerarquicas.reduce((sum, p) => sum + (p.hijos?.length || 0), 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FolderTree className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Categorías</p>
                <p className="text-2xl font-bold">{categoriasJerarquicas.length + categoriasJerarquicas.reduce((sum, p) => sum + (p.hijos?.length || 0), 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Categories List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FolderTree className="w-5 h-5" />
            Estructura de Categorías
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : categoriasJerarquicas.length === 0 ? (
            <div className="text-center py-12">
              <FolderTree className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No hay categorías</p>
              <p className="text-sm text-gray-400 mt-1">Haz clic en "Inicializar Categorías" para crear la estructura predefinida</p>
            </div>
          ) : (
            <div className="divide-y">
              {categoriasJerarquicas.map((padre) => (
                <Collapsible key={padre.id} open={expandedPadres[padre.id]} onOpenChange={() => toggleExpanded(padre.id)}>
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                    <CollapsibleTrigger className="flex items-center gap-3 flex-1 text-left">
                      {expandedPadres[padre.id] ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                      <div className="flex items-center gap-2">
                        <Layers className="w-4 h-4 text-emerald-600" />
                        <span className="font-semibold text-gray-800">{padre.nombre}</span>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {padre.hijos?.length || 0} subcategorías
                        </Badge>
                      </div>
                    </CollapsibleTrigger>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); handleOpenDialog(null, { preselectedPadreId: padre.id }); }}
                        title="Agregar subcategoría"
                      >
                        <Plus className="w-4 h-4 text-emerald-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleOpenDialog(padre); }}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(padre); }}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <CollapsibleContent>
                    {padre.hijos && padre.hijos.length > 0 ? (
                      <Table>
                        <TableBody>
                          {padre.hijos.map((hijo) => (
                            <TableRow key={hijo.id} className="hover:bg-gray-50">
                              <TableCell className="pl-14">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                                  <span>{hijo.nombre}</span>
                                </div>
                              </TableCell>
                              <TableCell className="w-24">
                                <Badge className={hijo.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                                  {hijo.activo ? 'Activo' : 'Inactivo'}
                                </Badge>
                              </TableCell>
                              <TableCell className="w-24 text-right">
                                <div className="flex justify-end gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(hijo)}>
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(hijo)}>
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="px-14 py-4 text-sm text-gray-400 italic">
                        Sin subcategorías
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Crear/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tags className="w-5 h-5 text-emerald-600" />
              {editingItem ? 'Editar Categoría' : 'Nueva Categoría'}
            </DialogTitle>
            <DialogDescription>
              {formData.padre_id ? 'Subcategoría dentro de una categoría padre' : 'Categoría padre principal'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label>Categoría Padre</Label>
              <Select 
                value={formData.padre_id ? String(formData.padre_id) : "none"} 
                onValueChange={(v) => setFormData({ ...formData, padre_id: v === "none" ? null : parseInt(v) })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Ninguna (es categoría padre)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ninguna (es categoría padre)</SelectItem>
                  {categoriasPadre.filter(p => !editingItem || p.id !== editingItem.id).map(p => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nombre *</Label>
              <Input 
                value={formData.nombre} 
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} 
                placeholder={formData.padre_id ? "Ej: Alquileres Oficina" : "Ej: Gasto Administrativo"}
                className="mt-1"
                required 
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmar Eliminar */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar categoría?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm?.padre_id 
                ? `Se eliminará la subcategoría "${deleteConfirm?.nombre}".`
                : `Se eliminará la categoría padre "${deleteConfirm?.nombre}" y todas sus subcategorías.`
              }
              <br />Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar Inicializar */}
      <AlertDialog open={initConfirm} onOpenChange={setInitConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Inicializar Categorías
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se crearán las siguientes categorías padre con sus subcategorías:
              <ul className="mt-2 text-sm list-disc list-inside space-y-1">
                <li>Gasto de Venta (8 subcategorías)</li>
                <li>Gasto Administrativo (9 subcategorías)</li>
                <li>Gasto de Producción (8 subcategorías)</li>
                <li>Gasto de Distribución (8 subcategorías)</li>
                <li>Gasto de Marketing (8 subcategorías)</li>
                <li>Gasto de Diseño y Patronaje (8 subcategorías)</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleInicializar} className="bg-amber-600 hover:bg-amber-700">
              <Sparkles className="w-4 h-4 mr-2" />
              Crear Categorías
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CategoriasPage;
